import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Route, Plus, Trash2, Paperclip, FileText, Loader2, ChevronDown, StickyNote, X, BookOpen, Wand2, ExternalLink, BellRing, ShoppingBag, Armchair } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

/* "Your Journey, Organized Every Step of the Way" — per-journey stage checklists
   (Getting There → Origin → Transit(s) → Destination). Every task has its own
   tick box, note and photo/document attachments. */

const ARTICLE = {
  title: "Checklist & Information at Every Segment of Your Journey",
  paras: [
    "Travel is more than moving from origin to destination. Each trip includes multiple stages, each with its own information, tasks, timings, documents, and reminders.",
    "My Travel Hub brings all stages together in one place within your trip. Whether traveling by air, train, bus, ferry, car, or other transport, your journey is organized into clear sections linked to each segment.",
  ],
  stages: [
    ["Getting to the Airport / Station / Terminal", "Assembling all your bags, plan transport, timing, parking, drop-off, departure time, and related details."],
    ["At the Origin Airport / Station / Terminal", "Keep check-in, baggage, security, boarding, gate/platform, lounge, food, shopping, and other pre-departure tasks in one place."],
    ["At Transit Airports / Stations / Terminals", "Manage connections — arrival details, terminal/platform changes, connection times, security or immigration, lounges, meals, and next departure. If going out, plan immigration, customs, transport, hotel transfer, rental car, pickup details, hotel or friends' addresses, contacts and other arrival tasks. Have multiple connections? Add a Transit section for each stop."],
    ["At the Destination Airport / Station / Terminal", "Organize arrival essentials such as immigration, baggage claim, customs, transport, hotel transfer, rental car, pickup details, addresses, contacts, and other arrival tasks."],
  ],
  closing: "Instead of searching through emails, screenshots, notes, tickets, apps, and messages, My Travel Hub keeps everything linked to the right stage of your journey. Check off tasks, see what's next, and access key information as you move through each segment. From departure to arrival, My Travel Hub keeps you organized every step of the way.",
};

export default function JourneyChecklist({ trip, readOnly }) {
  const [journeys, setJourneys] = useState(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/journey-checklist/trip/${trip.id}`)
    .then((r) => setJourneys(r.data.items)).catch(() => setJourneys([]));
  useEffect(() => { load(); }, [trip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createJourney = async (name) => {
    setBusy(true);
    try {
      await api.post(`/journey-checklist/trip/${trip.id}`, { name });
      await load();
      toast.success(`${name} created — tick tasks, add notes and attach photos or documents at every stage`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the journey"); }
    finally { setBusy(false); }
  };

  const removeJourney = async (j) => {
    if (!window.confirm(`Remove "${j.name}" and all its tasks, notes and attachments?`)) return;
    try { await api.delete(`/journey-checklist/${j.id}`); setActive(0); load(); } catch { toast.error("Could not remove"); }
  };

  if (!journeys) return null;
  const j = journeys[active];

  return (
    <section id="journey-checklist" className="mb-10 scroll-mt-24" data-testid="journey-checklist">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h2 className="font-heading font-900 text-sky-700 text-2xl flex items-center gap-2">
          <Route className="w-6 h-6" /> Your Journey, Organized Every Step of the Way
        </h2>
      </div>
      <ArticleIntro />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {journeys.map((x, i) => (
          <button key={x.id} onClick={() => setActive(i)} data-testid={`journey-tab-${x.id}`}
            className={`px-3 py-1.5 text-sm font-700 border transition-colors ${i === active
              ? "bg-sky-700 border-sky-700 text-white" : "bg-white border-zinc-300 text-zinc-700 hover:border-sky-600"}`}>
            {x.name}
          </button>
        ))}
        {!readOnly && journeys.length < 6 && (
          <button onClick={() => createJourney(journeys.length === 0 ? "Outbound journey" : journeys.length === 1 ? "Return journey" : `Journey ${journeys.length + 1}`)}
            disabled={busy} data-testid="journey-add-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 border border-sky-500 text-sky-700 hover:bg-sky-50 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {journeys.length === 0 ? "Start journey checklist" : "Add journey (e.g. return)"}
          </button>
        )}
        {!readOnly && j && (
          <button onClick={() => removeJourney(j)} data-testid={`journey-delete-${j.id}`}
            className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-rose-600">
            <Trash2 className="w-3.5 h-3.5" /> Remove this journey
          </button>
        )}
      </div>

      {!j ? (
        <div className="border border-dashed border-zinc-300 p-6 text-center text-sm text-muted-foreground" data-testid="journey-empty">
          One trip. Every stage. One place. Start the journey checklist to organize Getting There, the Origin, every Transit and the Destination — with notes, photos and documents on every task.
        </div>
      ) : (
        <JourneyView journey={j} trip={trip} readOnly={readOnly} onChange={(updated) =>
          setJourneys((list) => list.map((x) => (x.id === updated.id ? updated : x)))} />
      )}
    </section>
  );
}

function ArticleIntro() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-sky-200 bg-sky-50/50 mb-4" data-testid="journey-article">
      <button onClick={() => setOpen((v) => !v)} data-testid="journey-article-toggle"
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
        <span className="text-sm font-700 text-sky-900">{ARTICLE.title}</span>
        <ChevronDown className={`w-4 h-4 ml-auto text-sky-700 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-zinc-700 space-y-3">
          {ARTICLE.paras.map((p, i) => <p key={i}>{p}</p>)}
          <div className="font-700 text-sky-900">Your Journey Stages</div>
          {ARTICLE.stages.map(([t, d]) => (
            <p key={t}><b>{t}</b><br />{d}</p>
          ))}
          <div className="font-700 text-sky-900">One Trip. Every Stage. One Place.</div>
          <p>{ARTICLE.closing}</p>
          <p className="font-700 text-emerald-700">Explore • Plan • Share • Travel</p>
        </div>
      )}
    </div>
  );
}

const KIND_COLORS = {
  getting_there: "text-orange-700 border-orange-300 bg-orange-50",
  origin: "text-blue-700 border-blue-300 bg-blue-50",
  transit: "text-violet-700 border-violet-300 bg-violet-50",
  destination: "text-emerald-700 border-emerald-300 bg-emerald-50",
};

const AIRSIDE_SUGGESTIONS = [
  "Eat before boarding",
  "Visit airport lounge",
  "Fill water bottle after security",
  "Charge phone & electronic devices",
  "Use restroom before boarding",
  "After boarding: move power bank into the seat pocket",
];

const DESTINATION_SUGGESTIONS = [
  "Retrieve power bank & belongings from the seat pocket",
  "Message family / pickup contact on landing",
];

const SUGGESTIONS_BY_KIND = {
  origin: AIRSIDE_SUGGESTIONS,
  transit: AIRSIDE_SUGGESTIONS,
  destination: DESTINATION_SUGGESTIONS,
};

const ALERT_OFFSETS = [90, 60, 45, 30, 15];

/* Airport IATA for a section, derived from the trip's flight/rail segments:
   origin -> first departure; transit N -> Nth arrival; destination -> last arrival. */
function sectionAirport(journey, trip, section, _idx) {
  const segs = (trip?.segments || [])
    .filter((s) => ["flight", "train", "rail"].includes(s.mode || "flight"))
    .sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`));
  if (!segs.length) return null;
  const iata = (v) => (typeof v === "string" && /^[A-Za-z]{3}$/.test(v.trim()) ? v.trim().toUpperCase() : null);
  if (section.kind === "origin") return iata(segs[0].from_place);
  if (section.kind === "destination") return iata(segs[segs.length - 1].to_place);
  if (section.kind === "transit") {
    const transitIdx = journey.sections.filter((s) => s.kind === "transit").findIndex((s) => s.id === section.id);
    return transitIdx >= 0 && segs[transitIdx] ? iata(segs[transitIdx].to_place) : null;
  }
  return null;
}

function LoungeFinder({ journey, section, airport, call }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!open && !data) {
      setLoading(true);
      try { const r = await api.get(`/airports/${airport}/lounges`); setData(r.data); }
      catch { setData({ count: 0, groups: [] }); }
      finally { setLoading(false); }
    }
    setOpen((v) => !v);
  };

  const addToChecklist = (l) => {
    const text = `Visit lounge: ${l.name}${l.terminal ? ` (${l.terminal})` : ""}`.slice(0, 300);
    call(() => api.post(`/journey-checklist/${journey.id}/sections/${section.id}/tasks`, { text }));
    toast.success(`"${l.name}" added to this stage's checklist`);
  };

  return (
    <div className="border-b border-zinc-200 bg-indigo-50/40" data-testid={`lounge-finder-${section.id}`}>
      <button onClick={toggle} data-testid={`lounge-finder-toggle-${section.id}`}
        className="w-full flex items-center gap-2 px-4 py-2 text-left text-xs font-700 text-indigo-800">
        <Armchair className="w-3.5 h-3.5" />
        Lounges at {airport}
        {data && <span className="font-mono text-indigo-500">({data.count})</span>}
        {loading ? <Loader2 className="w-3.5 h-3.5 ml-auto animate-spin" /> :
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && data && (
        <div className="px-4 pb-3">
          {data.count === 0 ? (
            <p className="text-xs text-muted-foreground">No lounges in our database for {airport} yet.</p>
          ) : (
            <div className="space-y-2">
              {data.groups.map((g) => (
                <div key={g.key}>
                  <div className="text-[10px] font-mono uppercase tracking-wide text-indigo-500 mb-1">{g.label}</div>
                  <div className="space-y-1">
                    {g.items.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 bg-white border border-indigo-100 px-2.5 py-1.5" data-testid={`lounge-${l.id}`}>
                        <span className="text-xs flex-1 min-w-0">
                          <span className="font-700 text-zinc-800">{l.name}</span>
                          <span className="text-muted-foreground"> — {[l.terminal, l.gate_area].filter(Boolean).join(" · ") || l.operator}</span>
                        </span>
                        <button onClick={() => addToChecklist(l)} data-testid={`lounge-add-${l.id}`}
                          className="shrink-0 text-[11px] font-700 text-indigo-700 border border-indigo-300 px-2 py-0.5 hover:bg-indigo-50">
                          + Add to checklist
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {data.disclaimer && <p className="text-[10px] text-indigo-400 mt-1">{data.disclaimer}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BoardingAlerts({ journey, readOnly, call }) {
  const alerts = journey.alerts || { offsets: [], email: true, push: true };
  const save = (patch) => call(() => api.patch(`/journey-checklist/${journey.id}/alerts`, patch));
  const toggleOffset = (o) => save({
    offsets: alerts.offsets.includes(o) ? alerts.offsets.filter((x) => x !== o) : [...alerts.offsets, o],
  });
  return (
    <div className="mb-4 border border-amber-300 bg-amber-50/60 px-4 py-2.5" data-testid="boarding-alerts">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-700 text-amber-800">
          <BellRing className="w-4 h-4" /> Boarding alerts
        </span>
        {ALERT_OFFSETS.map((o) => (
          <button key={o} disabled={readOnly} onClick={() => toggleOffset(o)} data-testid={`boarding-alert-${o}`}
            className={`px-2.5 py-1 text-xs font-700 border transition-colors disabled:opacity-50 ${alerts.offsets.includes(o)
              ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-amber-300 text-amber-800 hover:border-amber-500"}`}>
            {o} min
          </button>
        ))}
        <label className="inline-flex items-center gap-1 text-xs font-600 text-amber-900 ml-2">
          <input type="checkbox" checked={alerts.email !== false} disabled={readOnly}
            onChange={(e) => save({ email: e.target.checked })} data-testid="boarding-alert-email" className="accent-amber-600" /> Email
        </label>
        <label className="inline-flex items-center gap-1 text-xs font-600 text-amber-900">
          <input type="checkbox" checked={alerts.push !== false} disabled={readOnly}
            onChange={(e) => save({ push: e.target.checked })} data-testid="boarding-alert-push" className="accent-amber-600" /> Push
        </label>
      </div>
      <p className="text-[11px] text-amber-700/90 mt-1">
        {alerts.offsets.length
          ? `We'll remind you ${[...alerts.offsets].sort((a, b) => b - a).join(", ")} minutes before boarding (needs the section's date & boarding/departure time filled in).`
          : "Pick when to be reminded before boarding — we use the boarding/departure times saved in each section."}
      </p>
    </div>
  );
}

function ShoppingList({ journey, readOnly, call }) {
  const [form, setForm] = useState({ name: "", qty: "", store: "", terminal: "" });
  const items = journey.shopping || [];
  const bought = items.filter((i) => i.bought).length;

  const add = async () => {
    if (!form.name.trim()) return;
    try {
      const { data } = await api.post(`/journey-checklist/${journey.id}/shopping`, form);
      await call(() => Promise.resolve({ data }));
      setForm({ name: "", qty: "", store: "", terminal: "" }); // clear only on success
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the item"); }
  };

  return (
    <div className="mt-6 border border-teal-300 bg-white" data-testid="journey-shopping">
      <div className="flex flex-wrap items-center gap-2 border-b border-teal-200 bg-teal-50 px-4 py-2.5 text-teal-800">
        <h3 className="font-heading font-900 text-base inline-flex items-center gap-2">
          <ShoppingBag className="w-5 h-5" /> Airport Shopping List
        </h3>
        <span className="text-xs font-mono">{bought}/{items.length} bought</span>
      </div>
      {items.length > 0 && (
        <div className="divide-y divide-zinc-100">
          {items.map((i) => (
            <div key={i.id} className="flex items-center gap-3 px-4 py-2" data-testid={`shopping-item-${i.id}`}>
              {!readOnly ? (
                <input type="checkbox" checked={i.bought} data-testid={`shopping-bought-${i.id}`}
                  onChange={() => call(() => api.patch(`/journey-checklist/${journey.id}/shopping/${i.id}`, { bought: !i.bought }))}
                  className="w-4 h-4 accent-teal-600 cursor-pointer" />
              ) : (
                <span className={`w-4 h-4 border ${i.bought ? "bg-teal-600 border-teal-600" : "border-zinc-300"}`} />
              )}
              <span className={`text-sm flex-1 ${i.bought ? "line-through text-zinc-400" : "text-zinc-800"}`}>
                {i.name}{i.qty && <span className="text-muted-foreground"> × {i.qty}</span>}
                {(i.store || i.terminal) && (
                  <span className="text-xs text-muted-foreground"> — {[i.store, i.terminal && `T${i.terminal}`].filter(Boolean).join(", ")}</span>
                )}
              </span>
              {!readOnly && (
                <button onClick={() => call(() => api.delete(`/journey-checklist/${journey.id}/shopping/${i.id}`))}
                  data-testid={`shopping-delete-${i.id}`} aria-label="Remove item">
                  <Trash2 className="w-3.5 h-3.5 text-zinc-400 hover:text-rose-600" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!readOnly && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 border-t border-teal-100 bg-teal-50/40 px-4 py-2.5">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Item *" maxLength={120}
            onKeyDown={(e) => e.key === "Enter" && add()} data-testid="shopping-name"
            className="col-span-2 border border-zinc-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:border-teal-600" />
          <input value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} placeholder="Qty" maxLength={20}
            data-testid="shopping-qty" className="border border-zinc-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:border-teal-600" />
          <input value={form.store} onChange={(e) => setForm({ ...form, store: e.target.value })} placeholder="Store / Terminal" maxLength={80}
            data-testid="shopping-store" className="border border-zinc-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:border-teal-600" />
          <button onClick={add} data-testid="shopping-add"
            className="bg-teal-700 text-white text-sm font-700 py-1.5 hover:bg-teal-600">Add item</button>
        </div>
      )}
      <p className="px-4 pb-2 text-[10px] text-teal-700/80">Unbought items stay on the list for your next airport or destination.</p>
    </div>
  );
}

function JourneyView({ journey, trip, readOnly, onChange }) {
  const call = async (fn) => {
    try { const { data } = await fn(); onChange(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const addTransit = () => call(() => api.post(`/journey-checklist/${journey.id}/sections`, { kind: "transit" }));
  const total = journey.sections.reduce((n, s) => n + s.tasks.length, 0);
  const done = journey.sections.reduce((n, s) => n + s.tasks.filter((t) => t.done).length, 0);

  return (
    <div data-testid={`journey-view-${journey.id}`}>
      <div className="mb-4 text-sm font-700 text-zinc-700" data-testid="journey-progress">
        {done} of {total} travel tasks completed
        <div className="h-1.5 bg-zinc-100 border border-zinc-200 mt-1 max-w-sm">
          <div className="h-full bg-sky-600 transition-[width]" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
        </div>
      </div>
      <BoardingAlerts journey={journey} readOnly={readOnly} call={call} />
      <div className="space-y-6">
        {journey.sections.map((s, idx) => (
          <SectionCard key={s.id} journey={journey} section={s} readOnly={readOnly} call={call}
            airport={sectionAirport(journey, trip, s, idx)} />
        ))}
      </div>
      <ShoppingList journey={journey} readOnly={readOnly} call={call} />
      {!readOnly && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={addTransit} data-testid="journey-add-transit"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 border border-violet-500 text-violet-700 hover:bg-violet-50">
            <Plus className="w-4 h-4" /> Add another transit stop
          </button>
          <button onClick={() => call(() => api.post(`/journey-checklist/${journey.id}/autofill`)).then(() =>
            toast.success("Filled empty details from your trip segments, airport contacts and destination guides"))}
            data-testid="journey-autofill"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 border border-sky-500 text-sky-700 hover:bg-sky-50">
            <Wand2 className="w-4 h-4" /> Auto-fill from trip
          </button>
        </div>
      )}
    </div>
  );
}

function SectionCard({ journey, section, readOnly, call, airport }) {
  const [adding, setAdding] = useState("");
  const doneCount = section.tasks.filter((t) => t.done).length;
  const color = KIND_COLORS[section.kind] || KIND_COLORS.origin;

  const addTask = () => {
    if (!adding.trim()) return;
    call(() => api.post(`/journey-checklist/${journey.id}/sections/${section.id}/tasks`, { text: adding.trim() }));
    setAdding("");
  };

  return (
    <div className="border border-zinc-300 bg-white" data-testid={`journey-section-${section.id}`}>
      <div className={`flex flex-wrap items-center gap-2 border-b px-4 py-2.5 ${color}`}>
        <h3 className="font-heading font-900 text-base">{section.title}</h3>
        <span className="text-xs font-mono">{doneCount}/{section.tasks.length}</span>
        {section.guide && (
          <Link to={section.guide.url} data-testid={`journey-guide-link-${section.id}`}
            className="inline-flex items-center gap-1 text-xs font-700 bg-white/70 border border-current px-2 py-0.5 hover:bg-white transition-colors"
            title={`Destination guide — read at leisure`}>
            <BookOpen className="w-3.5 h-3.5" /> Read about {section.guide.name}
          </Link>
        )}
        {!readOnly && section.kind === "transit" && (
          <button onClick={() => window.confirm("Remove this transit stop and its tasks?") &&
            call(() => api.delete(`/journey-checklist/${journey.id}/sections/${section.id}`))}
            data-testid={`journey-section-delete-${section.id}`}
            className="ml-auto text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100">
            <Trash2 className="w-3.5 h-3.5" /> Remove stop
          </button>
        )}
      </div>
      {section.kind !== "getting_there" && (
        <SectionDetails journey={journey} section={section} readOnly={readOnly} call={call} />
      )}
      {airport && ["origin", "transit"].includes(section.kind) && (
        <LoungeFinder journey={journey} section={section} airport={airport} call={call} />
      )}
      <div className="divide-y divide-zinc-100">
        {section.tasks.map((t) => (
          <TaskRow key={t.id} journey={journey} task={t} readOnly={readOnly} call={call} />
        ))}
      </div>
      {!readOnly && (
        <div className="flex flex-col gap-2 border-t border-zinc-200 bg-zinc-50 px-4 py-2.5">
          {SUGGESTIONS_BY_KIND[section.kind] && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-mono uppercase text-muted-foreground">Quick add:</span>
              {SUGGESTIONS_BY_KIND[section.kind].filter((sug) => {
                const key = sug.toLowerCase().slice(0, 30); // normalized prefix — hides chips whose equivalent template task exists
                return !section.tasks.some((t) => t.text.toLowerCase().startsWith(key));
              }).map((sug, si) => (
                <button key={sug} data-testid={`journey-suggest-${section.id}-${si}`}
                  onClick={() => call(() => api.post(`/journey-checklist/${journey.id}/sections/${section.id}/tasks`, { text: sug }))}
                  className="text-[11px] font-600 border border-dashed border-teal-400 text-teal-700 bg-teal-50/60 px-2 py-0.5 hover:bg-teal-50 transition-colors">
                  + {sug}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input value={adding} onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTask()} maxLength={300}
              placeholder="Add your own task for this stage…" data-testid={`journey-task-input-${section.id}`}
              className="flex-1 border border-zinc-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-sky-600" />
            <button onClick={addTask} data-testid={`journey-task-add-${section.id}`}
              className="px-3 py-1.5 bg-sky-700 text-white text-sm font-700 hover:bg-sky-600">Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

const DETAIL_FIELDS = [
  ["flight", "Flight / Train no."], ["carrier", "Airline / Train"], ["terminal", "Terminal"],
  ["gate", "Gate / Platform"], ["departure_time", "Departure time"], ["boarding_time", "Boarding time"],
  ["arrival_time", "Arrival time"], ["seat", "Seat"], ["booking_ref", "Booking / ticket ref"],
  ["carrier_link", "Airline / airport link"], ["contact_phone", "Contact phone"], ["contact_email", "Contact email"],
];
const DOC_CATS = [["ticket", "Ticket copy"], ["boarding_pass", "Boarding pass"], ["baggage_tag", "Baggage tag"], ["other", "Other"]];
const DOC_LABEL = Object.fromEntries(DOC_CATS);

function SectionDetails({ journey, section, readOnly, call }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(section.details || {});
  const [uploadingCat, setUploadingCat] = useState(null);
  const fileRef = useRef(null);
  const catRef = useRef("other");
  useEffect(() => { setD(section.details || {}); }, [section.details]);

  const docs = section.docs || [];
  const filled = DETAIL_FIELDS.filter(([k]) => (section.details || {})[k]).length;

  const save = async () => {
    await call(() => api.patch(`/journey-checklist/${journey.id}/sections/${section.id}/details`, d));
    toast.success("Details saved");
  };

  const pick = (cat) => { catRef.current = cat; fileRef.current?.click(); };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("File must be under 12 MB"); return; }
    setUploadingCat(catRef.current);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/journey-checklist/${journey.id}/sections/${section.id}/docs?category=${catRef.current}`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      await call(() => Promise.resolve({ data }));
      toast.success(`${DOC_LABEL[catRef.current]} attached`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingCat(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="border-b border-zinc-200 bg-zinc-50/60" data-testid={`journey-details-${section.id}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`journey-details-toggle-${section.id}`}
        className="w-full flex items-center gap-2 px-4 py-2 text-left text-xs font-700 text-zinc-700">
        <FileText className="w-3.5 h-3.5 text-sky-700" />
        Flight / terminal details, tickets, boarding pass &amp; baggage tags
        <span className="font-mono text-muted-foreground">({filled} details · {docs.length} docs)</span>
        <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {DETAIL_FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{label}</span>
                <input value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} disabled={readOnly}
                  maxLength={120} data-testid={`journey-detail-${k}-${section.id}`}
                  className="w-full border border-zinc-300 bg-white px-2 py-1 text-sm focus:outline-none focus:border-sky-600 disabled:bg-zinc-100" />
                {k === "carrier_link" && (d[k] || "").startsWith("http") && (
                  <a href={d[k]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[10px] text-sky-700 hover:underline">
                    Open link <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </label>
            ))}
          </div>
          <label className="block mt-2">
            <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">Notes</span>
            <textarea value={d.notes || ""} onChange={(e) => setD({ ...d, notes: e.target.value })} disabled={readOnly}
              rows={2} maxLength={2000} data-testid={`journey-detail-notes-${section.id}`}
              placeholder="Anything to remember at this stop — desk locations, contacts, confirmations…"
              className="w-full border border-zinc-300 bg-white px-2 py-1 text-sm focus:outline-none focus:border-sky-600 disabled:bg-zinc-100" />
          </label>
          {!readOnly && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button onClick={save} data-testid={`journey-details-save-${section.id}`}
                className="px-3 py-1.5 bg-sky-700 text-white text-xs font-700 hover:bg-sky-600">Save details</button>
              {DOC_CATS.map(([cat, label]) => (
                <button key={cat} onClick={() => pick(cat)} disabled={!!uploadingCat}
                  data-testid={`journey-doc-add-${cat}-${section.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-zinc-300 bg-white text-xs font-600 text-zinc-700 hover:border-sky-500 disabled:opacity-50">
                  {uploadingCat === cat ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} {label}
                </button>
              ))}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={upload} />
            </div>
          )}
          {docs.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {docs.map((a) => (
                <span key={a.id} className="relative group inline-flex items-center gap-1 border border-zinc-300 bg-white px-2 py-1 text-xs" data-testid={`journey-doc-${a.id}`}>
                  {a.kind === "photo"
                    ? <a href={`${API}${a.url}`} target="_blank" rel="noreferrer"><img src={`${API}${a.url}`} alt={a.name} className="w-10 h-10 object-cover border border-zinc-200" loading="lazy" /></a>
                    : <FileText className="w-3.5 h-3.5 text-rose-600" />}
                  <span className="flex flex-col">
                    <a href={`${API}${a.url}`} target="_blank" rel="noreferrer" className="hover:underline max-w-[120px] truncate font-600">{a.name}</a>
                    <span className="text-[9px] font-mono uppercase text-muted-foreground">{DOC_LABEL[a.category] || a.category}</span>
                  </span>
                  {!readOnly && (
                    <button onClick={() => call(() => api.delete(`/journey-checklist/${journey.id}/sections/${section.id}/docs/${a.id}`))}
                      data-testid={`journey-doc-delete-${a.id}`} aria-label="Remove document">
                      <X className="w-3 h-3 text-zinc-400 hover:text-rose-600" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskRow({ journey, task, readOnly, call }) {
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState(task.note || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [optimisticDone, setOptimisticDone] = useState(null); // instant tick while PATCH is in flight
  const done = optimisticDone ?? task.done;
  const toggle = () => {
    setOptimisticDone(!done);
    call(() => api.patch(`/journey-checklist/${journey.id}/tasks/${task.id}`, { done: !done }))
      .finally(() => setOptimisticDone(null));
  };
  const saveNote = () => {
    call(() => api.patch(`/journey-checklist/${journey.id}/tasks/${task.id}`, { note }));
    setShowNote(false);
    toast.success("Note saved");
  };
  const remove = () => window.confirm("Delete this task (and its notes/attachments)?") &&
    call(() => api.delete(`/journey-checklist/${journey.id}/tasks/${task.id}`));

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("File must be under 12 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/journey-checklist/${journey.id}/tasks/${task.id}/attachments`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      call(() => Promise.resolve({ data }));
      toast.success("Attached — photos get hidden metadata (EXIF/GPS) removed automatically");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="px-4 py-2.5" data-testid={`journey-task-${task.id}`}>
      <div className="flex items-start gap-3">
        {!readOnly ? (
          <input type="checkbox" checked={done} onChange={toggle} data-testid={`journey-task-check-${task.id}`}
            className="mt-1 w-5 h-5 accent-sky-700 cursor-pointer shrink-0" />
        ) : (
          <span className={`mt-1 w-5 h-5 border shrink-0 ${done ? "bg-sky-700 border-sky-700" : "border-zinc-300"}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${done ? "line-through text-zinc-400" : "text-zinc-800"}`}>{task.text}</div>
          {task.note && !showNote && (
            <div className="mt-1 text-xs text-amber-900 bg-amber-50 border border-amber-200 px-2 py-1 whitespace-pre-wrap" data-testid={`journey-task-note-${task.id}`}>
              {task.note}
            </div>
          )}
          {showNote && (
            <div className="mt-1.5">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000}
                placeholder="Details for this task — booking refs, contacts, addresses, pickup times…"
                data-testid={`journey-task-note-input-${task.id}`}
                className="w-full border border-zinc-300 px-2.5 py-1.5 text-sm focus:outline-none focus:border-sky-600" />
              <div className="flex gap-2 mt-1">
                <button onClick={saveNote} data-testid={`journey-task-note-save-${task.id}`}
                  className="px-3 py-1 bg-sky-700 text-white text-xs font-700">Save note</button>
                <button onClick={() => { setShowNote(false); setNote(task.note || ""); }} className="px-3 py-1 border border-zinc-300 text-xs">Cancel</button>
              </div>
            </div>
          )}
          {task.attachments?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {task.attachments.map((a) => a.kind === "photo" ? (
                <span key={a.id} className="relative group">
                  <a href={`${API}${a.url}`} target="_blank" rel="noreferrer" data-testid={`journey-attach-${a.id}`}>
                    <img src={`${API}${a.url}`} alt={a.name} className="w-14 h-14 object-cover border border-zinc-200" loading="lazy" />
                  </a>
                  {!readOnly && (
                    <button onClick={() => call(() => api.delete(`/journey-checklist/${journey.id}/tasks/${task.id}/attachments/${a.id}`))}
                      data-testid={`journey-attach-delete-${a.id}`} aria-label="Remove attachment"
                      className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 shadow-sm hover:border-rose-400">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ) : (
                <span key={a.id} className="inline-flex items-center gap-1 border border-zinc-300 bg-zinc-50 px-2 py-1 text-xs">
                  <FileText className="w-3.5 h-3.5 text-rose-600" />
                  <a href={`${API}${a.url}`} target="_blank" rel="noreferrer" className="hover:underline max-w-[140px] truncate" data-testid={`journey-attach-${a.id}`}>{a.name}</a>
                  {!readOnly && (
                    <button onClick={() => call(() => api.delete(`/journey-checklist/${journey.id}/tasks/${task.id}/attachments/${a.id}`))}
                      data-testid={`journey-attach-delete-${a.id}`} aria-label="Remove attachment">
                      <X className="w-3 h-3 text-zinc-400 hover:text-rose-600" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => setShowNote((v) => !v)} title="Add a note" data-testid={`journey-task-note-btn-${task.id}`}
              className={`p-1.5 border transition-colors ${task.note ? "border-amber-400 bg-amber-50 text-amber-700" : "border-zinc-200 text-zinc-400 hover:text-amber-700 hover:border-amber-400"}`}>
              <StickyNote className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Attach photo or document (PDF)"
              data-testid={`journey-task-attach-btn-${task.id}`}
              className="p-1.5 border border-zinc-200 text-zinc-400 hover:text-sky-700 hover:border-sky-400 transition-colors disabled:opacity-50">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
            </button>
            <button onClick={remove} title="Delete task" data-testid={`journey-task-delete-${task.id}`}
              className="p-1.5 border border-zinc-200 text-zinc-400 hover:text-rose-600 hover:border-rose-400 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={upload} />
          </div>
        )}
      </div>
    </div>
  );
}
